import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  setLocale as setI18nLocale,
  isLocaleSupported,
  getLocaleDisplayName,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  type SupportedLocale
} from '@/i18n'
import { useUserProfileStore } from '@/stores/userProfile'
import { Language } from '@/api/generated-users'
import type { ClientLanguage } from '@/api/generated-portal'
import { logUnexpectedFailure } from '@/utils/errorHandling'

/**
 * Storage key for locale preference when not authenticated.
 */
const LOCALE_STORAGE_KEY = 'scrivio-locale'

/**
 * Initialize locale from localStorage or browser detection.
 *
 * This is a standalone function that can be called outside of a Vue component context,
 * unlike the useLocale() composable which requires the setup() context due to useI18n().
 *
 * Use this in places like Firebase auth callbacks where there's no Vue component context.
 */
export async function initLocaleFromStorage(): Promise<void> {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
  if (stored && isLocaleSupported(stored)) {
    await setI18nLocale(stored)
  } else {
    // No stored preference: use Dutch as default for this Dutch-focused application.
    // Browser language detection is not used because E2E tests run in English browsers
    // and we want unauthenticated users to see Dutch by default.
    await setI18nLocale(DEFAULT_LOCALE)
    localStorage.setItem(LOCALE_STORAGE_KEY, DEFAULT_LOCALE)
  }
}

/**
 * Initialize locale from a user's language preference, or from the language a
 * cliënt's dossier records when the cliëntportaal signs them in.
 *
 * This is a standalone function that can be called outside of a Vue component context.
 * Use this after fetching a user profile in places like Firebase auth callbacks.
 *
 * @param language - The language from the user profile (e.g., Language.NL, Language.EN)
 */
export async function initLocaleFromProfile(
  language: Language | ClientLanguage | null | undefined
): Promise<void> {
  if (!language) {
    // No language preference in profile, fall back to storage/defaults
    await initLocaleFromStorage()
    return
  }

  const locale = language.toLowerCase()
  if (isLocaleSupported(locale)) {
    await setI18nLocale(locale)
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } else {
    // Unsupported language in profile, fall back to storage/defaults
    await initLocaleFromStorage()
  }
}

/**
 * Composable for managing application locale.
 *
 * For authenticated users, persists preference via user profile API.
 * For non-authenticated users, uses localStorage.
 */
export function useLocale() {
  const { locale } = useI18n()
  const profileStore = useUserProfileStore()

  /**
   * Current locale value.
   */
  const currentLocale = computed<SupportedLocale>(() => {
    const value = locale.value
    return isLocaleSupported(value) ? value : DEFAULT_LOCALE
  })

  /**
   * Available locales with display names.
   */
  const availableLocales = computed(() =>
    SUPPORTED_LOCALES.map(loc => ({
      value: loc,
      label: getLocaleDisplayName(loc)
    }))
  )

  /**
   * Change locale and persist the preference.
   *
   * For authenticated users: saves to user profile preferences.
   * For non-authenticated users: saves to localStorage.
   */
  async function changeLocale(newLocale: SupportedLocale): Promise<void> {
    if (!isLocaleSupported(newLocale)) {
      console.warn(`Unsupported locale: ${newLocale}`)
      return
    }

    await setI18nLocale(newLocale)

    // Always store in localStorage as backup
    localStorage.setItem(LOCALE_STORAGE_KEY, newLocale)

    // If authenticated, persist to user profile's language field
    if (profileStore.profile) {
      try {
        await profileStore.updateProfile({
          language: newLocale.toUpperCase() as Language
        })
      } catch (err) {
        // Log but don't fail - locale change already applied locally
        logUnexpectedFailure('Failed to save locale preference:', err)
      }
    }
  }

  /**
   * Watch for profile language changes to sync locale preference.
   * When user logs in or their language changes, update the app locale.
   */
  watch(
    () => profileStore.profile?.language,
    async (language) => {
      if (language) {
        const locale = language.toLowerCase()
        if (isLocaleSupported(locale) && locale !== currentLocale.value) {
          await setI18nLocale(locale)
          localStorage.setItem(LOCALE_STORAGE_KEY, locale)
        }
      }
    }
  )

  return {
    currentLocale,
    availableLocales,
    changeLocale
  }
}
